import {
  AuthenticationResult,
  Configuration,
  EndSessionRequest,
  NavigationClient,
  PublicClientApplication,
  SilentRequest,
} from "@azure/msal-browser";
import localforage from "localforage";
import { getEnvConfig } from "~/services/appConfig";
import { AuthError, AuthErrorType, MaybeAccount } from "~/types/auth";
import { clearMsalStorage } from "~/utils/authRecovery";
import { isInIframe, requestTokenFromParent } from "~/utils/iframeAuth";

// Avoid circular dependency by not importing authStore directly
let authStoreSetup: (() => Promise<any>) | undefined;

// Configuration for retry logic
interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
};

/**
 * MSAL instance and state management
 */
let msal: PublicClientApplication | undefined;
let knownScopes: string[] = [];
let knownConfig: Configuration;
let initialized = false;
let initializationPromise: Promise<any> | undefined;

// Check if iframe authentication is blocked (persisted in sessionStorage)
const getIframeBlocked = (): boolean => {
  return sessionStorage.getItem("iframe_blocked") === "true";
};

const setIframeBlocked = (blocked: boolean): void => {
  if (blocked) {
    sessionStorage.setItem("iframe_blocked", "true");
  } else {
    sessionStorage.removeItem("iframe_blocked");
  }
};

// Basic state tracking
interface AuthState {
  isInitializing: boolean;
  isLoggingIn: boolean;
  isAcquiringToken: boolean;
}

const authState: AuthState = {
  isInitializing: false,
  isLoggingIn: false,
  isAcquiringToken: false,
};

// Token acquisition coordination
let tokenAcquisitionPromise: Promise<string | null> | null = null;
let lastTokenAcquisitionTime = 0;
const TOKEN_ACQUISITION_COOLDOWN = 1000; // 1 second cooldown between attempts

// Local token cache to prevent excessive network requests when MSAL cache fails
let localCachedToken: string | null = null;
let localTokenExpiresOn: number = 0;

/**
 * Utility function for exponential backoff delay
 */
const calculateDelay = (attempt: number, config: RetryConfig): number => {
  const delay = Math.min(
    config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1),
    config.maxDelay,
  );
  // Add jitter to prevent thundering herd
  return delay + Math.random() * 1000;
};

const redirectCountKey = "redirectCount";

/**
 * Enhanced error classification
 */
const classifyError = (error: any): AuthError => {
  const message = error.message ?? error.errorMessage ?? "Unknown error";

  if (
    message.includes("X-Frame-Options") ||
    message.includes("frame") ||
    message.includes("iframe")
  ) {
    return new AuthError(
      AuthErrorType.FRAME_BLOCKED,
      "Frame-based authentication blocked - using redirect flow instead",
      error,
    );
  }

  if (message.includes("reprocess") || message.includes("redirect loop")) {
    return new AuthError(
      AuthErrorType.REDIRECT_LOOP,
      "Redirect loop detected",
      error,
    );
  }

  if (error.errorCode === "access_denied") {
    if (message.includes("AADB2C90091")) {
      return new AuthError(
        AuthErrorType.ACCESS_DENIED,
        "Access denied - login required",
        error,
      );
    }
    if (message.includes("AADB2C90118")) {
      return new AuthError(
        AuthErrorType.PASSWORD_RESET_REQUIRED,
        "Password reset required",
        error,
      );
    }
  }

  // Handle MSAL token request cache errors
  if (
    error.errorCode === "no_token_request_cache_error" ||
    message.includes("no_token_request_cache_error") ||
    message.includes("No token request found in cache")
  ) {
    return new AuthError(
      AuthErrorType.TOKEN_ACQUISITION_FAILED,
      "Authentication state lost - please try logging in again",
      error,
    );
  }

  // Handle session expiry errors
  if (
    error.errorCode === "interaction_required" ||
    message.includes("AADB2C90077")
  ) {
    return new AuthError(
      AuthErrorType.TOKEN_ACQUISITION_FAILED,
      "Session expired - re-authentication required",
      error,
    );
  }

  if (error.errorCode === "interaction_in_progress") {
    return new AuthError(
      AuthErrorType.INTERACTION_IN_PROGRESS,
      "Authentication interaction in progress",
      error,
    );
  }

  return new AuthError(AuthErrorType.TOKEN_ACQUISITION_FAILED, message, error);
};

/**
 * Enhanced auth service with improved error handling and retry logic
 */
export const authService = {
  /**
   * Initialize and return active account
   */
  async initialize(
    client: NavigationClient,
    config: Configuration,
    scopes: string[],
  ): Promise<MaybeAccount> {
    // If initialization is already in progress, wait for it
    if (initializationPromise) {
      try {
        await initializationPromise;
        return msal?.getActiveAccount() || null;
      } catch {
        console.warn("Previous initialization failed, retrying...");
        initializationPromise = undefined;
      }
    }

    if (authState.isInitializing) {
      throw new AuthError(
        AuthErrorType.INITIALIZATION_FAILED,
        "Initialization already in progress",
      );
    }

    // If already initialized with the same config, just return the active account
    if (
      initialized &&
      msal &&
      knownConfig?.auth?.clientId === config.auth.clientId
    ) {
      return msal.getActiveAccount();
    }

    authState.isInitializing = true;

    // Create and store the initialization promise
    initializationPromise = (async () => {
      try {
        // Save scopes and config
        knownScopes = scopes;
        knownConfig = config;

        // Initialize MSAL
        msal ??= new PublicClientApplication(config);
        await msal.initialize();

        // Set navigation client
        if (client) {
          msal.setNavigationClient(client);
        }

        // Handle redirect promise - this processes any authentication response in the URL
        const redirectResult = await this.handleRedirectWithRetry();

        if (redirectResult && redirectResult.account) {
          msal.setActiveAccount(redirectResult.account);

          // Clean up the URL hash after successfully handling the redirect.
          // MSAL leaves auth params (state, code, client_info) in the hash
          // which confuses vue-router's hash-based routing.
          const currentHash = globalThis.location.hash;
          if (
            currentHash.includes("state=") ||
            currentHash.includes("code=") ||
            currentHash.includes("client_info=")
          ) {
            globalThis.location.hash = "#/";
          }
        }

        // Set active account if available (fallback if redirect didn't return one)
        if (!msal.getActiveAccount()) {
          const accounts = msal.getAllAccounts();
          if (accounts?.length) {
            msal.setActiveAccount(accounts[0]);
          }
        }

        initialized = true;
        authState.isInitializing = false;
        initializationPromise = undefined; // Clear the promise

        return msal.getActiveAccount();
      } catch (error) {
        authState.isInitializing = false;
        initializationPromise = undefined; // Clear the promise on error
        const authError = classifyError(error);

        // For token cache errors, clear authentication state and return null
        // This allows the app to continue and prompt for re-authentication
        if (authError.message.includes("Authentication state lost")) {
          console.warn(
            "Token request cache error detected, clearing auth state",
          );
          this.clearAuthenticationState();
          return null;
        }

        // User cancelled the login/signup (AADB2C90091) - not an error,
        // allow the app to continue as unauthenticated.
        if (authError.type === AuthErrorType.ACCESS_DENIED) {
          console.info(
            "User cancelled authentication - continuing as unauthenticated",
          );
          this.clearAuthenticationState();
          return null;
        }

        throw authError;
      }
    })();

    return initializationPromise;
  },

  /**
   * Handle redirect promise with basic retry logic
   */
  async handleRedirectWithRetry(
    retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG,
  ): Promise<AuthenticationResult | null> {
    if (!msal) {
      throw new AuthError(
        AuthErrorType.INITIALIZATION_FAILED,
        "MSAL is not initialized",
      );
    }

    // Check for potential redirect loop with more sophisticated detection
    const redirectLoopKey = "lastRedirectTime";
    const currentTime = Date.now();
    const lastRedirectTime = Number.parseInt(
      sessionStorage.getItem(redirectLoopKey) || "0",
    );
    const redirectCount = Number.parseInt(
      sessionStorage.getItem(redirectCountKey) || "0",
    );

    // Reset count if enough time has passed
    if (currentTime - lastRedirectTime > 3000) {
      // 3 seconds
      sessionStorage.setItem(redirectCountKey, "0");
    }

    // Check for rapid redirects (more than 3 in 3 seconds)
    if (currentTime - lastRedirectTime < 3000 && redirectCount >= 3) {
      console.warn("Redirect loop detected - clearing auth state and stopping");
      this.clearAuthenticationState();
      sessionStorage.removeItem(redirectLoopKey);
      sessionStorage.removeItem(redirectCountKey);
      throw new AuthError(
        AuthErrorType.REDIRECT_LOOP,
        "Too many redirects detected - authentication loop prevented",
      );
    }

    // Update tracking
    sessionStorage.setItem(redirectLoopKey, currentTime.toString());
    sessionStorage.setItem(redirectCountKey, (redirectCount + 1).toString());

    for (let attempt = 1; attempt <= retryConfig.maxAttempts; attempt++) {
      try {
        if (attempt > 1) {
          console.log(
            `Retrying redirect (${attempt}/${retryConfig.maxAttempts})`,
          );
        }
        const result = await msal.handleRedirectPromise();

        if (result && result.account) {
          msal.setActiveAccount(result.account);
        }

        // Clear interaction flags after handling redirect (whether successful or not)
        sessionStorage.removeItem("auth_interaction_in_progress");
        sessionStorage.removeItem("lastRedirectTime");
        sessionStorage.removeItem("redirectCount");

        return result;
      } catch (error) {
        const authError = classifyError(error);

        // Handle specific error types
        switch (authError.type) {
          case AuthErrorType.FRAME_BLOCKED:
          case AuthErrorType.REDIRECT_LOOP:
            msal.clearCache();
            this.resetAuthState();
            return null;

          case AuthErrorType.ACCESS_DENIED:
            // User cancelled the login/signup (AADB2C90091) - not an error,
            // just clear the auth state and allow the app to continue unauthenticated.
            this.clearAuthenticationState();
            return null;

          case AuthErrorType.PASSWORD_RESET_REQUIRED:
            await this.handlePasswordReset();
            return null;

          default:
            if (attempt === retryConfig.maxAttempts) {
              throw authError;
            }

            await new Promise((resolve) =>
              setTimeout(resolve, calculateDelay(attempt, retryConfig)),
            );
        }
      }
    }

    throw new AuthError(
      AuthErrorType.MAX_RETRIES_EXCEEDED,
      "Maximum retry attempts exceeded",
    );
  },

  /**
   * Clear any stuck interaction states
   */
  clearInteractionState(): void {
    console.log("Clearing interaction state...");

    // Clear session storage flags
    sessionStorage.removeItem("auth_interaction_in_progress");
    sessionStorage.removeItem("lastRedirectTime");
    sessionStorage.removeItem("redirectCount");
    setIframeBlocked(false); // Clear iframe blocked flag

    // Clear token acquisition coordination state
    tokenAcquisitionPromise = null;
    lastTokenAcquisitionTime = 0;

    // Clear local token cache
    localCachedToken = null;
    localTokenExpiresOn = 0;

    // Reset auth state flags
    authState.isLoggingIn = false;
    authState.isAcquiringToken = false;

    // Clear all MSAL interaction-related keys from sessionStorage and localStorage by pattern.
    // MSAL v4 may store its interaction lock under keys like "{clientId}.interaction.status"
    // which the private browserStorage API may not reliably clear.
    // We only target keys that are clearly MSAL-owned (contain "msal" or are prefixed
    // with the known client ID) to avoid removing unrelated application state.
    try {
      const clientId = knownConfig?.auth?.clientId;
      if (clientId) {
        // Remove the specific key MSAL v4 uses for interaction status
        sessionStorage.removeItem(`${clientId}.interaction.status`);
        localStorage.removeItem(`${clientId}.interaction.status`);
      }

      const filterInteractionKeys = (k: string) => {
        const lk = k.toLowerCase();
        return (
          lk.includes("interaction") &&
          (lk.includes("msal") ||
            (clientId !== null &&
              clientId !== undefined &&
              k.startsWith(clientId)))
        );
      };

      const interactionKeysSession = Object.keys(sessionStorage).filter(
        filterInteractionKeys,
      );
      interactionKeysSession.forEach((k) => sessionStorage.removeItem(k));

      const interactionKeysLocal = Object.keys(localStorage).filter(
        filterInteractionKeys,
      );
      interactionKeysLocal.forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      console.warn("Failed to clear interaction keys from storage:", e);
    }

    if (msal) {
      try {
        // Clear MSAL's internal interaction state via private API (best effort)
        const browserStorage = (msal as any).browserStorage;
        if (browserStorage) {
          browserStorage.removeItem("interaction.status");
          browserStorage.removeItem("interaction.inProgress");
          browserStorage.removeItem("msal.interaction.status");
          browserStorage.removeItem("msal.interaction.in.progress");
        }

        // Try to clear any pending interaction promises
        if ((msal as any).interactionInProgress) {
          (msal as any).interactionInProgress = false;
        }
      } catch (error) {
        console.warn("Failed to clear MSAL interaction state:", error);
      }
    }
  },

  /**
   * Handle password reset flow
   */
  async handlePasswordReset(): Promise<void> {
    if (!msal) {
      throw new AuthError(
        AuthErrorType.INITIALIZATION_FAILED,
        "MSAL is not initialized",
      );
    }

    await localforage.setItem("passwordResetFlow", true);

    const envConfig = getEnvConfig();
    const request = {
      redirectUri: knownConfig.auth.redirectUri,
      scopes: [],
      authority: `${envConfig.authAuthority}/${envConfig.authPasswordResetPolicyId}`,
    };

    // Add delay to prevent immediate redirect loops
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await msal.loginRedirect(request);
  },

  /**
   * Login with enhanced error handling and loop prevention
   */
  async login(): Promise<MaybeAccount> {
    if (!initialized) {
      throw new AuthError(
        AuthErrorType.INITIALIZATION_FAILED,
        "Auth service not initialized",
      );
    }

    // Check if we already have an account (prevent unnecessary logins)
    const existingAccount = msal?.getActiveAccount();
    if (existingAccount) {
      return existingAccount;
    }

    // Check for MSAL's internal interaction state first
    if (msal) {
      try {
        // Try different ways to check MSAL interaction status
        const interactionStatus =
          (msal as any).getInteractionStatus?.() ||
          (msal as any).interactionStatus ||
          "none";

        if (interactionStatus !== "none" && interactionStatus !== "None") {
          console.warn(
            "MSAL interaction already in progress:",
            interactionStatus,
          );
          throw new AuthError(
            AuthErrorType.INTERACTION_IN_PROGRESS,
            "Authentication interaction in progress",
          );
        }
      } catch (error) {
        console.warn("Could not check MSAL interaction status:", error);
        // Continue with login attempt
      }
    }

    if (authState.isLoggingIn) {
      console.warn("Login already in progress, skipping duplicate request");
      throw new AuthError(
        AuthErrorType.INTERACTION_IN_PROGRESS,
        "Login already in progress",
      );
    }

    authState.isLoggingIn = true;

    try {
      // Check for interaction flags and wait if needed
      const interactionKey = "auth_interaction_in_progress";
      const interactionInProgress =
        sessionStorage.getItem(interactionKey) === "true";

      if (interactionInProgress) {
        console.log("Waiting for existing interaction to complete...");
        let attempts = 0;
        const maxAttempts = 15; // Increased from 10

        while (
          sessionStorage.getItem(interactionKey) === "true" &&
          attempts < maxAttempts
        ) {
          await new Promise((resolve) => setTimeout(resolve, 500)); // Increased delay
          attempts++;
        }

        // If still in progress after waiting, clear the state
        if (sessionStorage.getItem(interactionKey) === "true") {
          console.warn("Interaction state stuck, clearing...");
          this.clearInteractionState();
        }
      }

      // Double-check MSAL state after waiting
      if (msal) {
        try {
          const interactionStatus =
            (msal as any).getInteractionStatus?.() ||
            (msal as any).interactionStatus ||
            "none";

          if (interactionStatus !== "none" && interactionStatus !== "None") {
            console.warn(
              "MSAL still shows interaction in progress after waiting:",
              interactionStatus,
            );
            throw new AuthError(
              AuthErrorType.INTERACTION_IN_PROGRESS,
              "Authentication interaction still in progress",
            );
          }
        } catch (error) {
          console.warn("Could not recheck MSAL interaction status:", error);
          // Continue with login attempt
        }
      }

      const request = {
        redirectUri: knownConfig.auth.redirectUri,
        scopes: [],
      };

      // Mark interaction start explicitly so other flows can wait
      sessionStorage.setItem("auth_interaction_in_progress", "true");
      sessionStorage.setItem("lastRedirectTime", Date.now().toString());

      console.log("Starting login redirect...");
      await msal!.loginRedirect(request);
      return null; // Result handled in handleRedirectPromise
    } catch (error) {
      const authError = classifyError(error);
      console.error("Login error:", authError.message);

      // Clear interaction state on error
      sessionStorage.removeItem("auth_interaction_in_progress");

      switch (authError.type) {
        case AuthErrorType.FRAME_BLOCKED:
          console.warn("Frame blocked during login, clearing cache");
          msal!.clearCache();
          this.resetAuthState();
          return null;

        case AuthErrorType.INTERACTION_IN_PROGRESS:
          console.warn(
            "Interaction in progress during login, waiting briefly and then allowing retry",
          );
          // Don't throw immediately, let the caller handle retry logic
          throw authError;

        default:
          throw authError;
      }
    } finally {
      // Clear the login state flag after a delay to allow redirect to complete
      setTimeout(() => {
        authState.isLoggingIn = false;
      }, 2000); // Increased delay
    }
  },

  /**
   * Silent login with enhanced error handling - uses redirect instead of iframe
   */
  async silentLogin(): Promise<MaybeAccount> {
    if (!initialized || !msal) {
      throw new AuthError(
        AuthErrorType.INITIALIZATION_FAILED,
        "Auth service not initialized",
      );
    }

    const activeAccount = msal.getActiveAccount();
    if (!activeAccount) {
      throw new AuthError(
        AuthErrorType.TOKEN_ACQUISITION_FAILED,
        "No active account for silent login",
      );
    }

    // Instead of using ssoSilent (which uses iframes), check if we have a valid account
    // and attempt token acquisition. If that fails, fall back to login redirect.
    try {
      // Try to get a token silently first (this doesn't use iframes for account validation)
      const silentRequest: SilentRequest = {
        scopes: knownScopes,
        account: activeAccount,
        forceRefresh: false,
      };

      const result = await msal.acquireTokenSilent(silentRequest);
      if (result && result.account) {
        msal.setActiveAccount(result.account);
        return result.account;
      }

      // If no result, the account is still valid, return it
      return activeAccount;
    } catch (error) {
      const authError = classifyError(error);

      // If token acquisition fails, fall back to login redirect
      if (authError.type === AuthErrorType.INTERACTION_IN_PROGRESS) {
        return this.login();
      }

      // For other errors, still return the active account if it exists
      // The token acquisition will be handled separately
      console.warn(
        "Silent login token acquisition failed, but account exists:",
        authError.message,
      );
      return activeAccount;
    }
  },

  /**
   * Enhanced token acquisition with coordination to prevent race conditions
   */
  async getAccessTokenAsync(): Promise<string | null> {
    // Check our local memory cache first to prevent excessive MSAL network requests
    const now = Date.now();
    // The token expiration is calculated purely client-side to avoid clock skew issues
    if (localCachedToken && localTokenExpiresOn > now) {
      return localCachedToken;
    }

    // If there's already a token acquisition in progress, wait for it
    if (tokenAcquisitionPromise) {
      // console.log(
      //   "Token acquisition already in progress, waiting for result...",
      // );
      try {
        return await tokenAcquisitionPromise;
      } catch (_error) {
        // If the shared promise failed, we'll try again below
        console.warn("Shared token acquisition failed, retrying...");
        tokenAcquisitionPromise = null;
      }
    }

    // Implement cooldown to prevent rapid successive attempts
    if (now - lastTokenAcquisitionTime < TOKEN_ACQUISITION_COOLDOWN) {
      // console.log("Token acquisition cooldown active, waiting...");
      await new Promise((resolve) =>
        setTimeout(
          resolve,
          TOKEN_ACQUISITION_COOLDOWN - (now - lastTokenAcquisitionTime),
        ),
      );
    }

    // Create a new token acquisition promise that other calls can share
    tokenAcquisitionPromise = this._performTokenAcquisition();
    lastTokenAcquisitionTime = now;

    try {
      const result = await tokenAcquisitionPromise;
      return result;
    } finally {
      // Clear the promise after completion (success or failure)
      tokenAcquisitionPromise = null;
    }
  },

  /**
   * Internal method that performs the actual token acquisition
   */
  async _performTokenAcquisition(): Promise<string | null> {
    // Check if there's already an interaction in progress
    const interactionInProgressKey = "auth_interaction_in_progress";
    if (sessionStorage.getItem(interactionInProgressKey) === "true") {
      console.warn(
        "Another authentication interaction is in progress, waiting...",
      );

      // Wait for the interaction to complete, then try again
      let attempts = 0;
      const maxAttempts = 30; // Wait up to 30 seconds

      while (
        sessionStorage.getItem(interactionInProgressKey) === "true" &&
        attempts < maxAttempts
      ) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        attempts++;
      }

      if (attempts >= maxAttempts) {
        console.warn("Interaction timeout - clearing stuck interaction state");
        // Clear our flag AND MSAL's internal state to unblock subsequent token requests
        this.clearInteractionState();
        throw new AuthError(
          AuthErrorType.INTERACTION_IN_PROGRESS,
          "Authentication interaction timeout",
        );
      }

      // Recursively call this method now that interaction is complete
      return this._performTokenAcquisition();
    }

    if (!initialized || !msal) {
      if (authStoreSetup) {
        await authStoreSetup();
      }

      if (!initialized || !msal) {
        throw new AuthError(
          AuthErrorType.INITIALIZATION_FAILED,
          "Failed to initialize auth service",
        );
      }
    }

    const account = msal.getActiveAccount();
    if (!account) {
      // console.warn("No active account found for token acquisition");
      return null;
    }

    try {
      const passwordResetFlow = await localforage.getItem("passwordResetFlow");
      const envConfig = getEnvConfig();

      const silentRequest: SilentRequest = {
        scopes: knownScopes,
        account: account,
        authority: passwordResetFlow
          ? `${envConfig.authAuthority}/${envConfig.authPasswordResetPolicyId}`
          : undefined,
        forceRefresh: false,
      };

      // Clear password reset flag
      await localforage.setItem("passwordResetFlow", false);

      // If we're in an iframe, use message passing to get token from parent
      if (isInIframe()) {
        // console.log("In iframe context, requesting token from parent window");
        try {
          const token = await requestTokenFromParent();
          if (token) {
            // console.log("Successfully received token from parent window");
            return token;
          } else {
            console.warn("No token received from parent window");
            return null;
          }
        } catch (error) {
          console.error("Failed to get token from parent window:", error);
          // Continue with normal flow as fallback
        }
      }

      // If iframe is blocked, skip silent token acquisition entirely
      if (getIframeBlocked()) {
        throw new AuthError(
          AuthErrorType.FRAME_BLOCKED,
          "Iframe authentication blocked - using redirect flow",
        );
      }

      // Normal flow - iframe not blocked
      const result = await msal.acquireTokenSilent(silentRequest);

      // Update local memory cache
      if (result && result.accessToken) {
        localCachedToken = result.accessToken;

        // MSAL computes expiresOn based on the client's clock and the token's expires_in,
        // so it already accounts for server/client clock skew.
        // We subtract 10 seconds to give ourselves a safe buffer.
        localTokenExpiresOn = result.expiresOn
          ? result.expiresOn.getTime() - 10 * 1000
          : Date.now() + 5 * 60 * 1000 - 10 * 1000;
      }

      return result.accessToken;
    } catch (error) {
      const authError = classifyError(error);
      console.error("Token acquisition failed:", authError.message);

      // Check for MSAL errors that indicate corrupted localStorage state
      const errorObj = error as any; // MSAL errors have errorCode, message, errorMessage properties
      const errorCode = errorObj?.errorCode ?? "";
      const message = errorObj?.message ?? errorObj?.errorMessage ?? "";

      const corruptionIndicators = [
        "no_token_request_cache_error",
        "invalid_state",
        "state_mismatch",
        "invalid_grant",
        "malformed_token",
      ];

      const isCorruptionError = corruptionIndicators.some(
        (indicator) =>
          errorCode.includes(indicator) || message.includes(indicator),
      );

      if (isCorruptionError) {
        console.warn(
          "🔧 MSAL error indicates corrupted auth state, clearing MSAL storage",
        );
        clearMsalStorage();
      }

      // Handle specific error cases that require user interaction
      if (this._requiresUserInteraction(error)) {
        console.log(
          "Token acquisition requires user interaction, initiating redirect...",
        );
        return await this._handleInteractiveTokenAcquisition(
          account,
          authError,
        );
      }

      // For other errors, just throw them
      throw authError;
    }
  },

  /**
   * Check if the error requires user interaction (login/consent)
   */
  _requiresUserInteraction(error: any): boolean {
    const message = error.message ?? error.errorMessage ?? "";
    const errorCode = error.errorCode ?? "";

    return (
      errorCode === "interaction_required" ||
      errorCode === "consent_required" ||
      errorCode === "login_required" ||
      message.includes("AADB2C90077") || // User does not have an existing session
      message.includes("AADB2C90118") || // Password reset required
      message.includes("AADB2C90091") // Access denied - login required
    );
  },

  /**
   * Handle token acquisition that requires user interaction
   */
  async _handleInteractiveTokenAcquisition(
    account: any,
    authError: AuthError,
  ): Promise<string | null> {
    const interactionInProgressKey = "auth_interaction_in_progress";

    // Handle frame-blocked errors by triggering redirect flow
    if (authError.type === AuthErrorType.FRAME_BLOCKED) {
      console.warn(
        "Frame-based authentication blocked, triggering redirect flow",
      );
      // Remember that iframe is blocked for future token requests
      setIframeBlocked(true);
    }

    try {
      // Set interaction in progress flag
      sessionStorage.setItem(interactionInProgressKey, "true");

      const redirectRequest = {
        scopes: knownScopes,
        account: account,
        redirectUri: knownConfig.auth.redirectUri,
      };

      await msal!.acquireTokenRedirect(redirectRequest);

      // This line will only execute if the redirect doesn't happen immediately
      sessionStorage.removeItem(interactionInProgressKey);

      // Throw an error to indicate that authentication is in progress
      throw new AuthError(
        AuthErrorType.INTERACTION_IN_PROGRESS,
        "Authentication redirect initiated",
      );
    } catch (redirectError) {
      sessionStorage.removeItem(interactionInProgressKey);

      // If the redirect itself fails due to interaction in progress,
      // that's expected - return null instead of throwing to avoid breaking the app
      if (
        redirectError &&
        typeof redirectError === "object" &&
        "errorCode" in redirectError &&
        redirectError.errorCode === "interaction_in_progress"
      ) {
        console.log(
          "Redirect failed due to interaction in progress - this is expected",
        );
        return null;
      }

      console.error("Token redirect failed:", redirectError);
      throw redirectError;
    }
  },

  /**
   * Enhanced logout
   */
  async logout(): Promise<void> {
    sessionStorage.setItem(redirectCountKey, "0"); // Reset redirect count on logout

    // Proactively clear any interaction flags so a new login can start immediately
    try {
      this.clearInteractionState();
      sessionStorage.removeItem("auth_interaction_in_progress");
      sessionStorage.removeItem("lastRedirectTime");
      sessionStorage.removeItem("redirectCount");
      authState.isLoggingIn = false;
    } catch {
      /* empty */
    }

    if (!msal) {
      console.warn("MSAL not initialized, clearing local state only");
      this.clearAuthenticationState();
      return;
    }

    try {
      sessionStorage.removeItem("currentOrg");
      sessionStorage.removeItem("currentOrgGuid");

      // Use the correct logout method for MSAL v4
      const logoutRequest: EndSessionRequest = {
        postLogoutRedirectUri: knownConfig?.auth?.redirectUri ?? "/",
      };

      // Try different logout methods based on MSAL version
      if (typeof msal.logoutRedirect === "function") {
        await msal.logoutRedirect(logoutRequest);
      } else if (typeof msal.logout === "function") {
        await msal.logout(logoutRequest);
      } else {
        console.warn("No logout method available, clearing local state only");
        this.clearAuthenticationState();
        // Redirect manually
        globalThis.location.href = logoutRequest.postLogoutRedirectUri ?? "/";
      }
    } catch (error) {
      console.error("Logout failed:", error);
      // Force clear state even if logout fails
      this.clearAuthenticationState();
    }
  },

  /**
   * Clear authentication state with enhanced cleanup
   */
  clearAuthenticationState(): void {
    if (msal) {
      try {
        msal.clearCache();
      } catch (error) {
        console.warn("Failed to clear MSAL cache:", error);
      }
    }

    // Clear all storage
    try {
      sessionStorage.removeItem("currentOrg");
      sessionStorage.removeItem("currentOrgGuid");

      // Clear interaction state
      this.clearInteractionState();
    } catch (error) {
      console.warn("Failed to clear storage:", error);
    }

    // Reset all state
    this.resetAuthState();
    initialized = false;
    msal = undefined;
  },

  /**
   * Reset internal auth state
   */
  resetAuthState(): void {
    authState.isInitializing = false;
    authState.isLoggingIn = false;
    authState.isAcquiringToken = false;

    // Clear token acquisition coordination state
    tokenAcquisitionPromise = null;
    lastTokenAcquisitionTime = 0;

    // Clear local token cache
    localCachedToken = null;
    localTokenExpiresOn = 0;
  },

  /**
   * Get active account
   */
  getActiveAccount(): MaybeAccount {
    return msal?.getActiveAccount() || null;
  },

  // Expose msal instance for debugging
  get msal() {
    return msal;
  },

  /**
   * Handle redirect (legacy method for compatibility)
   */
  async handleRedirect(): Promise<AuthenticationResult | null> {
    return this.handleRedirectWithRetry();
  },

  /**
   * Check if authentication interaction is currently in progress
   */
  isAuthenticationInProgress(): boolean {
    return (
      sessionStorage.getItem("auth_interaction_in_progress") === "true" ||
      tokenAcquisitionPromise !== null ||
      authState.isLoggingIn ||
      authState.isAcquiringToken
    );
  },

  /**
   * Set auth store setup function to avoid circular dependency
   */
  setAuthStoreSetup(setupFn: () => Promise<any>): void {
    authStoreSetup = setupFn;
  },

  /**
   * Validate current auth configuration
   */
  validateConfiguration(): {
    valid: boolean;
    issues: string[];
  } {
    const issues: string[] = [];

    // Only validate runtime config if initialized
    if (initialized) {
      if (!knownConfig?.auth?.clientId) {
        issues.push("Missing client ID in runtime config");
      }

      if (!knownConfig?.auth?.authority) {
        issues.push("Missing authority in runtime config");
      }

      if (!knownConfig?.auth?.redirectUri) {
        issues.push("Missing redirect URI in runtime config");
      }

      if (!knownScopes?.length) {
        issues.push("No scopes configured in runtime config");
      }
    }

    // Check if runtime config has been loaded
    try {
      const envConfig = getEnvConfig();
      if (!envConfig.authAuthority) {
        issues.push("Missing authAuthority in runtime config");
      }
      if (!envConfig.authUserAppId) {
        issues.push("Missing authUserAppId in runtime config");
      }
      if (!envConfig.authUserScope) {
        issues.push("Missing authUserScope in runtime config");
      }
      if (!envConfig.entraRedirect) {
        issues.push("Missing entraRedirect in runtime config");
      }
      if (!envConfig.authUserPolicyId) {
        issues.push("Missing authUserPolicyId in runtime config");
      }
      if (!envConfig.authDomain) {
        issues.push("Missing authDomain in runtime config");
      }
    } catch (_error) {
      issues.push("Runtime configuration not loaded");
    }

    return {
      valid: issues.length === 0,
      issues,
    };
  },

  /**
   * Emergency reset - force clear all authentication state
   */
  emergencyReset(): void {
    console.warn(
      "Emergency reset triggered - clearing all authentication state",
    );

    // Clear MSAL cache if available
    if (msal) {
      try {
        msal.clearCache();
      } catch (error) {
        console.warn(
          "Failed to clear MSAL cache during emergency reset:",
          error,
        );
      }
    }

    // Clear all storage
    try {
      // localforage.clear();
      // sessionStorage.clear();
      // localStorage.clear();
    } catch (error) {
      console.warn("Failed to clear storage during emergency reset:", error);
    }

    // Reset all internal state
    msal = undefined;
    initialized = false;
    initializationPromise = undefined;
    knownScopes = [];
    this.resetAuthState();
  },

  /**
   * Get health status of the auth service
   */
  getHealthStatus(): {
    initialized: boolean;
    hasActiveAccount: boolean;
    hasValidConfig: boolean;
    isInitializing: boolean;
    isLoggingIn: boolean;
    lastError?: string;
  } {
    const activeAccount = msal?.getActiveAccount();
    const configValidation = this.validateConfiguration();

    return {
      initialized,
      hasActiveAccount: !!activeAccount,
      hasValidConfig: configValidation.valid,
      isInitializing: authState.isInitializing,
      isLoggingIn: authState.isLoggingIn,
      lastError:
        configValidation.issues.length > 0
          ? configValidation.issues.join(", ")
          : undefined,
    };
  },

  /**
   * Clear cache and reinitialize the auth service
   */
  async clearCacheAndReinitialize(): Promise<void> {
    try {
      // Clear MSAL cache if available
      if (msal) {
        await msal.clearCache();
      }

      // Clear all storage
      await localforage.clear();
      sessionStorage.removeItem("currentOrg");
      sessionStorage.removeItem("currentOrgGuid");

      // Reset internal state
      msal = undefined;
      initialized = false;
      initializationPromise = undefined;
      knownScopes = [];
      this.resetAuthState();
    } catch (error) {
      console.error("Failed to clear cache and reinitialize:", error);
      throw new AuthError(
        AuthErrorType.INITIALIZATION_FAILED,
        "Failed to clear cache and reinitialize auth service",
      );
    }
  },

  /**
   * Clear iframe blocked flag to allow silent token acquisition
   */
  clearIframeBlocked(): void {
    setIframeBlocked(false);
  },
};
